import React, {useMemo, useState} from 'react'
import {
	Box,
	Chip,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from '@mui/material'
import VideocamIcon from '@mui/icons-material/Videocam'
import PlaceIcon from '@mui/icons-material/Place'
import RouteIcon from '@mui/icons-material/Route'
import {Link} from 'react-router-dom'
import {buildPathToHistorySingleVoyage} from '@containers/singleVoyage/views/links/linkToHistorySingleVoyage/helpers/buildPathToActive'
import moment from 'moment'
import {IDeviceRecordingFile} from '@containers/monitoringPlatform/geoZones/types'
import {DownloadRecordingButton} from '@containers/monitoringPlatform/geoZones/recordingsByPeriod/DownloadRecordingButton'
import {PlayRecordingButton} from '@containers/monitoringPlatform/geoZones/recordingsByPeriod/PlayRecordingButton'

interface IProps {
	recordings: IDeviceRecordingFile[]
	accountId: number
	singleVoyage?: boolean
}

type SortField = 'recordedAt' | 'stopName'
type SortOrder = 'asc' | 'desc'

export const DeviceRecordingsList = ({recordings, accountId, singleVoyage}: IProps) => {
	const [sortField, setSortField] = useState<SortField>('recordedAt')
	const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortField(field)
			setSortOrder(field === 'stopName' ? 'asc' : 'desc')
		}
	}

	const sortedRecordings = useMemo(() => {
		const list = [...recordings]
		const direction = sortOrder === 'asc' ? 1 : -1
		return list.sort((a, b) => {
			if (sortField === 'stopName') {
				// Записи без зупинки завжди в кінці, незалежно від напрямку
				const aStop = a.stopName ?? ''
				const bStop = b.stopName ?? ''
				if (!aStop && !bStop) return 0
				if (!aStop) return 1
				if (!bStop) return -1
				return aStop.localeCompare(bStop, 'uk') * direction
			}
			return (moment(a.recordedAt).valueOf() - moment(b.recordedAt).valueOf()) * direction
		})
	}, [recordings, sortField, sortOrder])

	if (!recordings?.length) {
		return null
	}

	return (
		<TableContainer>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sortDirection={sortField === 'recordedAt' ? sortOrder : false}>
							<TableSortLabel
								active={sortField === 'recordedAt'}
								direction={sortField === 'recordedAt' ? sortOrder : 'desc'}
								onClick={() => handleSort('recordedAt')}
							>
								Час запису
							</TableSortLabel>
						</TableCell>
						<TableCell>Камера</TableCell>
						<TableCell>Тривалість</TableCell>
						<TableCell>Розмір</TableCell>
						{singleVoyage && <TableCell>Разовий рейс</TableCell>}
						{singleVoyage && <TableCell>Зупинка</TableCell>}
						{!singleVoyage && <TableCell>Маршрут</TableCell>}
						{!singleVoyage && (
							<TableCell sortDirection={sortField === 'stopName' ? sortOrder : false}>
								<TableSortLabel
									active={sortField === 'stopName'}
									direction={sortField === 'stopName' ? sortOrder : 'asc'}
									onClick={() => handleSort('stopName')}
								>
									Геозона
								</TableSortLabel>
							</TableCell>
						)}
						<TableCell>Статус</TableCell>
						<TableCell align="center">Відео</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{sortedRecordings.map((recording) => (
						<TableRow key={recording.id} hover>
							<TableCell>
								<Tooltip title={moment(recording.recordedAt).format('DD.MM.YYYY HH:mm:ss')}>
									<Typography variant="body2" fontWeight={600} noWrap>
										{moment(recording.recordedAt).format('DD.MM.YYYY HH:mm')}
									</Typography>
								</Tooltip>
							</TableCell>
							<TableCell>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<VideocamIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
									<Typography variant="body2" noWrap>
										{recording.cameraId}
									</Typography>
								</Stack>
							</TableCell>
							<TableCell>{recording.duration}</TableCell>
							<TableCell>{recording.fileSizeMb?.toFixed(1)} МБ</TableCell>
							{singleVoyage && (
								<TableCell>
									{recording.voyageId != null ? (
										<Stack direction="row" alignItems="center" spacing={0.5}>
											<RouteIcon sx={{fontSize: 14, color: 'primary.main'}}/>
											<Typography
												component={Link}
												to={buildPathToHistorySingleVoyage({voyageId: recording.voyageId, accountId})}
												target="_blank"
												variant="body2"
												color="primary"
												noWrap
												sx={{textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
											>
												{recording.voyageTitle || `Рейс #${recording.voyageId}`}
											</Typography>
										</Stack>
									) : (
										<Typography variant="caption" color="text.disabled">
											—
										</Typography>
									)}
								</TableCell>
							)}
							{singleVoyage && (
								<TableCell>
									{recording.waypointId != null ? (
										<Stack direction="row" alignItems="center" spacing={0.5}>
											<PlaceIcon sx={{fontSize: 14, color: 'success.main'}}/>
											<Typography variant="body2" noWrap>
												{recording.waypointOrdinal != null && `${recording.waypointOrdinal}. `}
												{recording.waypointName || '—'}
											</Typography>
										</Stack>
									) : (
										<Typography variant="caption" color="text.disabled">
											—
										</Typography>
									)}
								</TableCell>
							)}
							{!singleVoyage && (
								<TableCell>
									{recording.routeName ? (
										<Typography variant="body2" noWrap>
											{recording.routeName}
										</Typography>
									) : (
										<Typography variant="caption" color="text.disabled">
											—
										</Typography>
									)}
								</TableCell>
							)}
							{!singleVoyage && (
								<TableCell>
									{recording.routePointId != null ? (
										<Stack direction="row" alignItems="center" spacing={0.5}>
											<PlaceIcon sx={{fontSize: 14, color: 'success.main'}}/>
											<Typography variant="body2" noWrap>
												{recording.stopName || '—'}
											</Typography>
										</Stack>
									) : (
										<Typography variant="caption" color="text.disabled">
											поза геозоною
										</Typography>
									)}
								</TableCell>
							)}
							<TableCell>
								<Chip
									label={recording.status}
									size="small"
									color={recording.status === 'UPLOADED' ? 'success' : 'default'}
									variant="outlined"
									sx={{height: 20, fontSize: 11}}
								/>
							</TableCell>
							<TableCell align="center">
								<Stack direction="row" justifyContent="center" spacing={0.5}>
									<PlayRecordingButton
										accountId={accountId}
										recording={recording}
									/>
									<DownloadRecordingButton
										accountId={accountId}
										recordingId={recording.id}
										fileName={recording.path?.split('/').pop()}
									/>
								</Stack>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

export default DeviceRecordingsList
